import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import UserMenu from './UserMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataExportImport = () => {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exportType, setExportType] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [conflictAction, setConflictAction] = useState('ask');
  const [importType, setImportType] = useState('full');

  const handleExport = async (type) => {
    try {
      setExporting(true);
      setExportType(type);
      
      const response = await axios.get(`${API}/admin/export/${type}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename based on type
      const filenames = {
        'projects': 'projects_export.xlsx',
        'users': 'users_export.xlsx', 
        'settings': 'system_settings_export.xlsx',
        'full': 'full_database_export.json'
      };
      
      link.download = filenames[type];
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert(`Экспорт ${type} успешно скачан!`);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Ошибка при экспорте: ' + (error.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setExporting(false);
      setExportType('');
    }
  };

  const handleExportToDrive = async (type) => {
    try {
      setExporting(true);
      setExportType(type);
      
      const response = await axios.post(`${API}/admin/export/to-drive?export_type=${type}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success) {
        alert(`Экспорт ${type} успешно сохранен в Google Drive!\nФайл: ${response.data.file_name}`);
      }
      
    } catch (error) {
      console.error('Export to Drive failed:', error);
      alert('Ошибка при экспорте в Google Drive: ' + (error.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setExporting(false);
      setExportType('');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Пожалуйста, выберите файл для импорта');
      return;
    }
    
    try {
      setImporting(true);
      
      const formData = new FormData();
      formData.append('file', importFile);
      
      let endpoint;
      if (importType === 'projects') {
        endpoint = `${API}/admin/import/projects?action=${conflictAction}`;
      } else {
        endpoint = `${API}/admin/import/full?action=${conflictAction}`;
      }
      
      const response = await axios.post(endpoint, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      
      if (response.data.success) {
        let message = response.data.message;
        if (importType === 'projects' && response.data.field_mapping) {
          const mappedFields = Object.keys(response.data.field_mapping);
          message += `\n\nОпределенные поля: ${mappedFields.join(', ')}`;
        }
        alert(`Импорт завершен!\n${message}`);
        setImportFile(null);
      }
      
    } catch (error) {
      console.error('Import failed:', error);
      alert('Ошибка при импорте: ' + (error.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setImporting(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <UserMenu />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Доступ запрещён</h1>
            <p className="text-gray-600">Только администраторы могут управлять экспортом и импортом данных.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <UserMenu />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-6">
            <div className="flex items-center mb-6">
              <svg className="w-8 h-8 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Экспорт и Импорт Данных</h1>
                <p className="text-gray-600">Управление резервными копиями и миграцией данных</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Export Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m0-3v12" />
                </svg>
                Экспорт данных
              </h2>
              
              <div className="space-y-4">
                {/* Individual exports */}
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Отдельные экспорты (Excel)</h3>
                  <div className="grid grid-cols-1 gap-3">
                    <button
                      onClick={() => handleExport('projects')}
                      disabled={exporting && exportType === 'projects'}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      <div className="flex items-center">
                        <svg className="w-5 h-5 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        <div className="text-left">
                          <p className="font-medium">Проекты</p>
                          <p className="text-sm text-gray-500">Все проекты с задачами и комментариями</p>
                        </div>
                      </div>
                      {exporting && exportType === 'projects' && (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      )}
                    </button>
                    
                    <button
                      onClick={() => handleExport('users')}
                      disabled={exporting && exportType === 'users'}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      <div className="flex items-center">
                        <svg className="w-5 h-5 mr-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                        <div className="text-left">
                          <p className="font-medium">Пользователи</p>
                          <p className="text-sm text-gray-500">Все пользователи с ролями</p>
                        </div>
                      </div>
                      {exporting && exportType === 'users' && (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                      )}
                    </button>
                    
                    <button
                      onClick={() => handleExport('settings')}
                      disabled={exporting && exportType === 'settings'}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      <div className="flex items-center">
                        <svg className="w-5 h-5 mr-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <div className="text-left">
                          <p className="font-medium">Настройки</p>
                          <p className="text-sm text-gray-500">Системные настройки и шаблоны</p>
                        </div>
                      </div>
                      {exporting && exportType === 'settings' && (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600"></div>
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Full export */}
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Полная резервная копия (JSON)</h3>
                  <button
                    onClick={() => handleExport('full')}
                    disabled={exporting && exportType === 'full'}
                    className="w-full flex items-center justify-between p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 disabled:opacity-50"
                  >
                    <div className="flex items-center">
                      <svg className="w-6 h-6 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Полный экспорт базы данных</p>
                        <p className="text-sm text-gray-500">Все данные в JSON формате</p>
                      </div>
                    </div>
                    {exporting && exportType === 'full' && (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    )}
                  </button>
                </div>
                
                {/* Export to Google Drive */}
                <div className="mt-6 p-4 bg-green-50 rounded-lg">
                  <h3 className="text-lg font-medium text-green-800 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                    </svg>
                    Сохранить в Google Drive
                  </h3>
                  <p className="text-sm text-green-700 mb-3">Автоматическое сохранение резервных копий в Google Drive</p>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleExportToDrive('projects')}
                      disabled={exporting}
                      className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      Проекты в Drive
                    </button>
                    <button
                      onClick={() => handleExportToDrive('full')}
                      disabled={exporting}
                      className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      Полный в Drive
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Import Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l3-3m0 0l-3-3m0 3h12" />
                </svg>
                Импорт данных
              </h2>
              
              {/* File upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Выберите файл для импорта
                </label>
                
                {/* Import type selector */}
                <div className="mb-3">
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input 
                        type="radio" 
                        name="importType" 
                        value="full"
                        checked={importType === 'full'}
                        onChange={(e) => setImportType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Полная база (JSON)</span>
                    </label>
                    <label className="flex items-center">
                      <input 
                        type="radio" 
                        name="importType" 
                        value="projects"
                        checked={importType === 'projects'}
                        onChange={(e) => setImportType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Только проекты (Excel)</span>
                    </label>
                  </div>
                </div>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3-3m-3 3l3 3m-3-3H21m-7-4h3m-3 0h3m-3 0V9a4 4 0 014-4h4m0 0l3 3m-3-3l3 3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label htmlFor="import-file" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                        <span>Загрузить файл</span>
                        <input 
                          id="import-file" 
                          type="file" 
                          className="sr-only"
                          accept={importType === 'full' ? '.json' : '.xlsx,.xls'}
                          onChange={(e) => setImportFile(e.target.files[0])}
                        />
                      </label>
                      <p className="pl-1">или перетащите сюда</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      {importType === 'full' ? 'JSON файлы (полный экспорт базы данных)' : 'Excel файлы (.xlsx, .xls)'}
                    </p>
                  </div>
                </div>
                
                {importFile && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      Выбран файл: <span className="font-medium">{importFile.name}</span>
                    </p>
                  </div>
                )}
              </div>
              
              {/* Conflict resolution */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Обработка конфликтов
                </label>
                <select
                  value={conflictAction}
                  onChange={(e) => setConflictAction(e.target.value)}
                  className="form-select w-full"
                >
                  <option value="ask">Спросить при конфликтах</option>
                  <option value="overwrite">Перезаписать существующие</option>
                  <option value="new_id">Создать новые ID</option>
                </select>
              </div>
              
              {/* Import button */}
              <button
                onClick={handleImport}
                disabled={!importFile || importing}
                className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Импорт...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l3-3m0 0l-3-3m0 3h12" />
                    </svg>
                    Импортировать данные
                  </>
                )}
              </button>
              
              {/* Import result */}
              {importResult && (
                <div className={`mt-4 p-4 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <h4 className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    Результат импорта
                  </h4>
                  <p className={`text-sm mt-1 ${importResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {importResult.message}
                  </p>
                  
                  {importResult.import_stats && (
                    <div className="mt-2 text-sm text-gray-600">
                      <p>Пользователи: {importResult.import_stats.users.imported} импортировано, {importResult.import_stats.users.conflicts} конфликтов</p>
                      <p>Проекты: {importResult.import_stats.projects.imported} импортировано, {importResult.import_stats.projects.conflicts} конфликтов</p>
                      <p>Настройки: {importResult.import_stats.system_settings.imported} импортировано, {importResult.import_stats.system_settings.conflicts} конфликтов</p>
                    </div>
                  )}
                  
                  {importResult.conflicts && importResult.conflicts.length > 0 && (
                    <div className="mt-3">
                      <p className="font-medium text-yellow-800">Обнаружены конфликты:</p>
                      <ul className="text-sm text-yellow-700 mt-1">
                        {importResult.conflicts.slice(0, 5).map((conflict, index) => (
                          <li key={index}>• {conflict.identifier} ({conflict.type})</li>
                        ))}
                        {importResult.conflicts.length > 5 && (
                          <li>... и ещё {importResult.conflicts.length - 5} конфликтов</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Instructions */}
          <div className="mt-6 bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">ℹ️ Инструкции</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-800 mb-2">🔄 Экспорт</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>Excel экспорты</strong> - отдельные файлы для проектов, пользователей, настроек</li>
                  <li>• <strong>JSON экспорт</strong> - полная резервная копия всей базы данных</li>
                  <li>• <strong>Google Drive</strong> - автоматическое сохранение в ваш Google Drive</li>
                  <li>• <strong>Связи файлов</strong> - все file_id сохраняются для восстановления</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-2">📥 Импорт</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>Только JSON</strong> - полные резервные копии</li>
                  <li>• <strong>Конфликты</strong> - выберите стратегию обработки дубликатов</li>
                  <li>• <strong>File ID связи</strong> - автоматическое восстановление связей с Google Drive</li>
                  <li>• <strong>Безопасность</strong> - проверка структуры данных перед импортом</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataExportImport;